import { useState, useCallback, useContext, useRef, useEffect, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import IconButton from 'LuxKit/Button/IconButton'
import InputChip from 'LuxKit/Chips/InputChip'
import AssistChip from 'LuxKit/Chips/AssistChip'
import BodyText from 'LuxKit/Typography/BodyText'
import LineFileIcon from 'LuxKit/Icons/line/LineFileIcon'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import noop from 'lib/function/noop'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import useResizeObserver from 'hooks/useResizeObserver'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import useRebrandVersion from 'hooks/useRebrandVersion'
import useTextDirection from 'hooks/useTextDirection'
import { normaliseMessage } from 'tripPlanner/api/chatUtils'
import type { VoicePrompt } from './voicePromptTypes'
import { useVoicePrompt } from './hooks/useVoicePrompt'
import { useVoiceComposer } from './hooks/useVoiceComposer'
import { useSettleComposerFocus } from './hooks/useSettleComposerFocus'
import { useAddChatFiles } from './hooks/useAddChatFiles'
import { appendTranscript } from './utils/transcript'
import { COMPOSER_LINE_HEIGHT, COMPOSER_STACK_GAP } from './composerLayout'
import ComposerMicrophoneButton from './ComposerMicrophoneButton'
import VoiceChatNudge from './VoiceChatNudge'
import VoiceLandingTip from './VoiceLandingTip'
import RecordingOverlay from './RecordingOverlay'
import TextArea from 'components/Common/Form/Input/TextArea'
import ImageThumbnailPreview from './ImageThumbnailPreview'
import ChatInputShell from './ChatInputShell'
import ChatComposerAttachMenu from './ChatComposerAttachMenu'
import SelectedTagsModal from './SelectedTagsModal'
import MorphingSendButton from './MorphingSendButton'
import { ACCEPT_EXTENSIONS } from './chatFiles'
import ModalContext from 'contexts/ModalContext'

interface Props {
  onSubmit: (message: string) => void
  disabled?: boolean
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  disableSubmit?: boolean
  isStreaming?: boolean
  /**
   * When true, the composer is busy (streaming / finalising). Hides the mic and
   * keeps the send CTA disabled instead of offering a stop control.
   */
  showStopButton?: boolean
  files?: Array<File>
  onFilesChange?: (files: Array<File>) => void
  selectedTags?: Array<string>
  onAddBookingClick?: () => void
  showAddBookingAction?: boolean
  onRemoveTag?: (tag: string) => void
  inputRef?: React.RefObject<HTMLTextAreaElement | null>
  hideDisclaimer?: boolean
  compactDisclaimer?: boolean
  disclaimerText?: string
  autoFocus?: boolean
  /** Blur the textarea after submit everywhere. Soft-keyboard devices already blur regardless. */
  dismissKeyboardOnSubmit?: boolean
  /** Optional chatId for analytics tracking */
  chatId?: number
  /** Promotes voice input with this prompt and a pulsing mic */
  voicePrompt?: VoicePrompt
  /** False while the conversation can't take a new message */
  isVoicePromptReady?: boolean
  /** Floats the chat nudge over what sits above the composer instead of taking space */
  overlayVoicePrompt?: boolean
  /**
   * `stacked` puts the text above a controls row at every width, with roomier spacing, a labelled voice
   * button, and a recording overlay that shows its status beside the waveform.
   */
  layout?: 'inline' | 'stacked'
  /** `menu` opens a menu of attach actions; `picker` opens the file picker straight away. */
  attachmentMode?: 'menu' | 'picker'
  /**
   * Drawn in place of the placeholder text, which stays on the textarea for assistive tech. Hidden as a
   * placeholder would be: once there is text, while recording, and on focus on mobile.
   */
  placeholderOverlay?: React.ReactNode
}

const MAX_HEIGHT = 160
const ROWS = 1
// Selected tags stay on a single row so a few long suggestions can't push the
// conversation off screen; the rest live behind the "+n more" chip.
const MOBILE_VISIBLE_TAG_LIMIT = 2
const DESKTOP_VISIBLE_TAG_LIMIT = 3

const SelectedTagsGroup = styled(Group)`
  width: 100%;
  min-width: 0;
  overflow: hidden;
`

const ComposerInputChip = styled(InputChip)`
  min-width: 0;
  max-width: 100%;

  > *:not(svg):not(img) {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > svg {
    flex-shrink: 0;
  }
`

const MoreTagsChip = styled(AssistChip)`
  flex-shrink: 0;
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(COMPOSER_STACK_GAP)};
  width: 100%;
`

const FilePreviewItem = styled.div`
  position: relative;
`

const RemoveFileButton = styled.button`
  position: absolute;
  top: ${rem(-6)};
  inset-inline-end: ${rem(-6)};
  width: ${rem(20)};
  height: ${rem(20)};
  border-radius: 50%;
  border: none;
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 1;

  &:hover {
    background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  }
`

const FileCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${rem(8)};
  min-width: ${rem(160)};
  max-width: ${rem(240)};
`

const FileIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(40)};
  height: ${rem(40)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${rem(8)};
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

const FileInfoWrapper = styled.div`
  min-width: 0;
  flex: 1;
`

// Hidden file picker — no typed text, so Input dir handling does not apply.
const HiddenFileInput = styled.input`
  display: none;
`

const PlaceholderOverlay = styled.div`
  position: absolute;
  inset-block-start: 0;
  inset-inline: 0;
  line-height: ${rem(COMPOSER_LINE_HEIGHT)};
  pointer-events: none;
`

const TextAreaWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  width: 100%;

  &.stacked {
    align-items: flex-start;
  }

  label,
  span {
    width: 100%;
    min-width: 0;
    border: none;
  }

  textarea {
    height: ${rem(COMPOSER_LINE_HEIGHT)};
    min-height: ${rem(COMPOSER_LINE_HEIGHT)};
    padding: 0;
    line-height: ${rem(COMPOSER_LINE_HEIGHT)};
  }

  &.hide-placeholder-on-focus textarea:placeholder-shown {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  &.hide-placeholder-on-focus textarea:focus::placeholder {
    color: transparent;
  }

  &.hide-placeholder-on-focus:focus-within > ${PlaceholderOverlay} {
    visibility: hidden;
  }

  &.muted-placeholder textarea::placeholder {
    color: ${props => props.theme.palette.neutral.default.three};
  }

  &.has-placeholder-overlay textarea::placeholder {
    color: transparent;
  }
`

const StackableComposer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  grid-template-areas: 'attach textarea mic send';
  align-items: center;
  gap: ${rem(8)};
  width: 100%;

  &.stacked {
    grid-template-rows: auto auto;
    grid-template-areas:
      'textarea textarea textarea textarea'
      'attach . mic send';
    row-gap: ${rem(8)};
  }

  &.wide-gap {
    gap: ${rem(12)};
  }
`

const TextAreaSlot = styled.div`
  grid-area: textarea;
  min-width: 0;
`

const AttachSlot = styled.div`
  grid-area: attach;
  display: flex;
  align-items: center;
`

const MicSlot = styled.div`
  grid-area: mic;
  display: flex;
  align-items: center;
`

const SendSlot = styled.div`
  grid-area: send;
  display: flex;
  align-items: center;
`

const DisclaimerText = styled(BodyText)`
  &.compact {
    font-size: 10px;
    line-height: 13px;
  }
`

type ShellDensity = React.ComponentProps<typeof ChatInputShell>['density']

function getShellDensity(isStackedLayout: boolean, isMobile: boolean): ShellDensity {
  if (isStackedLayout) return 'comfortable'
  if (isMobile) return 'narrow'
  return 'regular'
}

const PAPERCLIP_ICON = <LinePaperclipIcon />

function ChatMessageInput({
  onSubmit,
  disabled = false,
  placeholder,
  value: controlledValue,
  onChange = noop,
  disableSubmit = false,
  isStreaming = false,
  showStopButton = isStreaming,
  files,
  onFilesChange,
  selectedTags,
  onAddBookingClick = noop,
  showAddBookingAction = true,
  onRemoveTag = noop,
  inputRef,
  hideDisclaimer = false,
  compactDisclaimer = false,
  disclaimerText: customDisclaimerText,
  autoFocus = true,
  dismissKeyboardOnSubmit = false,
  chatId,
  voicePrompt,
  isVoicePromptReady = true,
  overlayVoicePrompt = false,
  layout = 'inline',
  attachmentMode = 'menu',
  placeholderOverlay,
}: Props) {
  const intl = useIntl()
  const isRebrand = useRebrandVersion() === 'rebrand'

  const placeholderEmpty = intl.formatMessage({
    id: 'JreBnLG-TB3nexEa24pF',
    defaultMessage: 'Ask me anything',
    description: 'Placeholder text for empty chat input in TripPlanner',
  })
  const placeholderWithTags = intl.formatMessage({
    id: 'B1-3bpBEjRyHlrbaq_Qr',
    defaultMessage: 'Anything you’d like to add?',
    description: 'Placeholder text for chat input when tags are selected in TripPlanner',
  })
  const defaultDisclaimerText = intl.formatMessage({
    id: 'Bpg_QiPcqCREzrRFL2i-',
    defaultMessage: 'AI can make mistakes. Double-check important information.',
    description: 'AI accuracy disclaimer text displayed below assistant chat input',
  })
  const disclaimerText = customDisclaimerText ?? defaultDisclaimerText
  const attachFilesLabel = intl.formatMessage({
    id: 'uVQN888TEW3uyJMmN4tc',
    defaultMessage: 'Attach photos or files',
    description: 'Accessible label for the paperclip button on the TripPlanner landing page chat input that opens the file picker',
  })
  const [internalMessage, setInternalMessage] = useState('')
  const [microphoneElement, setMicrophoneElement] = useState<HTMLSpanElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const textDirection = useTextDirection(wrapperRef)
  const [isComposerMultiline, setIsComposerMultiline] = useState(false)
  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)
  // Device, not viewport: a narrow desktop window must keep Enter-to-send.
  const hasSoftKeyboard = useIsMobileOrTablet()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const stackableComposerRef = useRef<HTMLDivElement>(null)
  const latestMessageRef = useRef('')

  // Sync external ref with internal ref
  useEffect(() => {
    if (inputRef && textareaRef.current) {
      inputRef.current = textareaRef.current
    }
  })

  const fileInputRef = useRef<HTMLInputElement>(null)
  const message = controlledValue !== undefined ? controlledValue : internalMessage
  const hasMessage = Boolean(message.trim())
  const hasTags = Boolean(selectedTags?.length)
  const hasFiles = Boolean(files?.length)
  const hasContent = hasMessage || hasTags || hasFiles
  const placeholderText = placeholder ?? (hasTags ? placeholderWithTags : placeholderEmpty)

  latestMessageRef.current = message

  const resizeTextarea = useCallback(() => {
    if (!textareaRef.current) return
    const textarea = textareaRef.current
    textarea.style.height = 'auto'
    const scrollHeight = textarea.scrollHeight
    const newHeight = Math.min(scrollHeight, MAX_HEIGHT)
    textarea.style.height = `${newHeight}px`
    textarea.style.overflowY = scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden'

    if (isMobile) {
      const hasWrapped = scrollHeight > COMPOSER_LINE_HEIGHT + 1
      setIsComposerMultiline((prev) => {
        if (latestMessageRef.current.length === 0) return false
        return hasWrapped || prev
      })
    }
  }, [isMobile])

  const setMessage = useCallback((nextMessage: string) => {
    latestMessageRef.current = nextMessage
    if (controlledValue !== undefined) {
      onChange(nextMessage)
    } else {
      setInternalMessage(nextMessage)
    }
  }, [controlledValue, onChange])

  const resetMessage = useCallback(() => {
    setMessage('')
  }, [setMessage])

  const settleFocus = useSettleComposerFocus(textareaRef, dismissKeyboardOnSubmit)

  const submitMessage = useCallback((nextMessage: string) => {
    if (disabled || disableSubmit) return

    const processed = normaliseMessage(nextMessage)
    if (!processed && !hasTags && !hasFiles) return

    onSubmit(processed)
    resetMessage()
    requestAnimationFrame(resizeTextarea)
    settleFocus()
  }, [disableSubmit, disabled, hasFiles, hasTags, onSubmit, resetMessage, resizeTextarea, settleFocus])

  const handleTranscript = useCallback((transcript: string) => {
    setMessage(appendTranscript(latestMessageRef.current, transcript))
  }, [setMessage])

  const {
    isRecording,
    isTranscribing,
    waveformLevels,
    filledBars,
    startRecording,
    cancelRecording,
    submitRecording,
  } = useVoiceComposer({ chatId, onTranscript: handleTranscript, settleFocus })
  const isStackedLayout = layout === 'stacked'
  const usesStackableComposer = isMobile || isStackedLayout
  const isComposerStacked = isComposerMultiline || isStackedLayout
  const isVoiceBusy = isRecording || isTranscribing

  // Must run before paint: the wrap that triggers the stacked layout happens in the narrow
  // inline column, so deferring the measure to a frame later paints that wrapped-inline state.
  // Re-runs on isComposerMultiline so the height is re-measured at the wider stacked width.
  useIsomorphicLayoutEffect(() => {
    resizeTextarea()
  }, [message, isComposerMultiline, resizeTextarea])

  useEffect(() => {
    if (!isMobile) {
      setIsComposerMultiline(false)
    }
  }, [isMobile])

  useResizeObserver(usesStackableComposer ? stackableComposerRef : null, () => {
    requestAnimationFrame(resizeTextarea)
  })

  useEffect(() => {
    const stopContentScriptsFromFocus = (event: FocusEvent) => {
      if (textareaRef.current && event.target === textareaRef.current) {
        event.stopImmediatePropagation?.()
      }
    }

    document.addEventListener('focusin', stopContentScriptsFromFocus, true)
    return () => {
      document.removeEventListener('focusin', stopContentScriptsFromFocus, true)
    }
  }, [])

  const handleSubmit = useCallback(() => {
    submitMessage(message)
  }, [message, submitMessage])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // On a soft keyboard the return key composes a new line; the send button submits.
    if (event.key === 'Enter' && !event.shiftKey && !hasSoftKeyboard) {
      event.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit, hasSoftKeyboard])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value)
    // Measure in the same task as the keystroke; a deferred resize lets the browser paint
    // the wrapped-inline layout for a frame before the composer stacks.
    resizeTextarea()
  }, [resizeTextarea, setMessage])

  const addFiles = useAddChatFiles(files, onFilesChange)

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }

    addFiles(Array.from(event.target.files))

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [addFiles])

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!onFilesChange || disabled || isTranscribing) {
      return
    }

    // `files` already holds every file item, so reading `items` too would add each file twice.
    const pastedFiles = Array.from(event.clipboardData.files)
    if (pastedFiles.length === 0) {
      return
    }

    if (!event.clipboardData.getData('text')) {
      event.preventDefault()
    }
    addFiles(pastedFiles)
  }, [addFiles, disabled, isTranscribing, onFilesChange])

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleRemoveTag = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const tag = e.currentTarget.dataset.tag
    if (tag) onRemoveTag(tag)
  }, [onRemoveTag])

  const handleRemoveTagMouseDown = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const tag = e.currentTarget.dataset.tag
    if (tag) onRemoveTag(tag)
  }, [onRemoveTag])

  const handleShowMoreTags = useCallback(() => {
    if (!selectedTags?.length) return
    showModal(<SelectedTagsModal tags={selectedTags} onRemoveTag={onRemoveTag} />)
  }, [onRemoveTag, selectedTags, showModal])

  const handleRemoveFile = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const index = e.currentTarget.dataset.index
    if (index === undefined || !files || !onFilesChange) return
    const fileIndex = parseInt(index, 10)
    const newFiles = files.filter((_, i) => i !== fileIndex)
    onFilesChange(newFiles)
  }, [files, onFilesChange])

  const {
    isOpen: isVoicePromptOpen,
    isClosing: isVoicePromptClosing,
    isMicrophoneEmphasised,
    accept: acceptVoicePrompt,
    dismiss: dismissVoicePrompt,
    finishClosing: finishClosingVoicePrompt,
  } = useVoicePrompt({
    voicePrompt,
    canShow: isVoicePromptReady && !hasMessage && !disabled && !showStopButton && !isRecording && !isTranscribing,
    chatId,
  })

  const handleMicrophoneClick = useCallback(() => {
    if (isVoicePromptOpen) {
      acceptVoicePrompt()
    }
    startRecording()
  }, [acceptVoicePrompt, isVoicePromptOpen, startRecording])

  const handleVoicePromptDismiss = useCallback(() => {
    dismissVoicePrompt()
    settleFocus()
  }, [dismissVoicePrompt, settleFocus])

  const microphoneButton = useMemo(() => (
    <ComposerMicrophoneButton
      ref={setMicrophoneElement}
      onClick={handleMicrophoneClick}
      disabled={disabled}
      // The pulse ring would spread past the recording overlay that covers the button.
      isEmphasised={isMicrophoneEmphasised && !isVoiceBusy}
      labelled={isStackedLayout}
    />
  ), [disabled, handleMicrophoneClick, isMicrophoneEmphasised, isStackedLayout, isVoiceBusy])

  const ctaButton = useMemo(() => (
    <MorphingSendButton
      expanded={hasContent}
      iconOnly={isMobile}
      onClick={handleSubmit}
      disabled={disabled || disableSubmit || !hasContent || showStopButton}
    />
  ), [disabled, disableSubmit, handleSubmit, hasContent, isMobile, showStopButton])

  const recordingOverlay = useMemo(() => {
    if (!isVoiceBusy) {
      return null
    }

    return (
      <RecordingOverlay
        waveformLevels={waveformLevels}
        filledBars={filledBars}
        onCancel={cancelRecording}
        onSubmit={submitRecording}
        isTranscribing={isTranscribing}
        layout={layout}
      />
    )
  }, [
    cancelRecording,
    filledBars,
    isTranscribing,
    isVoiceBusy,
    layout,
    submitRecording,
    waveformLevels,
  ])

  const chipsContent = useMemo(() => {
    if (!hasTags || !selectedTags) return null

    const visibleTagLimit = isMobile ? MOBILE_VISIBLE_TAG_LIMIT : DESKTOP_VISIBLE_TAG_LIMIT
    const visibleTags = selectedTags.slice(0, visibleTagLimit)
    const hiddenTagsCount = selectedTags.length - visibleTags.length

    return (
      <SelectedTagsGroup direction="horizontal" gap={8} wrap="nowrap" fullWidth>
        {visibleTags.map((tag, index) => (
          <ComposerInputChip
            key={`${tag}-${index}`}
            variant="filled"
            data-tag={tag}
            title={tag}
            onClick={handleRemoveTag}
            onMouseDown={handleRemoveTagMouseDown}
            aria-label={intl.formatMessage(
              {
                id: 'Zb2W-kUOdVBBetSY_w-p',
                defaultMessage: 'Remove {tag}',
                description: 'Aria-label for button to remove a selected tag from chat input',
              },
              { tag },
            )}
          >
            {tag}
          </ComposerInputChip>
        ))}
        {hiddenTagsCount > 0 && (
          <MoreTagsChip
            size="small"
            variant="outline"
            onClick={handleShowMoreTags}
            aria-label={intl.formatMessage(
              {
                id: 'w3u8s-OS2brp5XbWgnYq',
                defaultMessage: 'Show {count} more tags',
                description: 'Aria-label for control that expands hidden selected tags in the TripPlanner chat composer',
              },
              { count: hiddenTagsCount },
            )}
          >
            {intl.formatMessage(
              {
                id: 'snr1OuXK2Bayg8-qYIA6',
                defaultMessage: '+{count} more',
                description: 'Link showing count of additional hidden selected tags in the TripPlanner chat composer',
              },
              { count: hiddenTagsCount },
            )}
          </MoreTagsChip>
        )}
      </SelectedTagsGroup>
    )
  }, [
    handleRemoveTag,
    handleRemoveTagMouseDown,
    handleShowMoreTags,
    hasTags,
    intl,
    isMobile,
    selectedTags,
  ])

  const filesContent = useMemo(() => {
    if (!files || files.length === 0) return null

    return (
      <Group direction="horizontal" gap={8} wrap="wrap">
        {files.map((file, index) => {
          const isImage = file.type.startsWith('image/')
          const fileExtension = file.name.split('.').pop()?.toUpperCase() || 'FILE'

          return (
            <FilePreviewItem key={`${file.name}-${index}`}>
              <RemoveFileButton
                data-index={index}
                onClick={handleRemoveFile}
                aria-label={intl.formatMessage(
                  {
                    id: 'e8YFBfyWPyJx0RyXqo69',
                    defaultMessage: 'Remove {fileName}',
                    description: 'Aria-label for button to remove an attached file from chat input',
                  },
                  { fileName: file.name },
                )}
              >
                <LineTimesIcon size="XXS" />
              </RemoveFileButton>
              {isImage && <ImageThumbnailPreview file={file} />}
              {!isImage && (
                <FileCard>
                  <FileIconWrapper>
                    <LineFileIcon size="M" />
                  </FileIconWrapper>
                  <FileInfoWrapper>
                    <Group direction="vertical" gap={2}>
                      <BodyText variant="medium" weight="bold" colour="normal-contrast" wrap="truncate" title={file.name}>
                        {file.name}
                      </BodyText>
                      <BodyText variant="small" colour="light-foreground-alt" format="uppercase">
                        {fileExtension}
                      </BodyText>
                    </Group>
                  </FileInfoWrapper>
                </FileCard>
              )}
            </FilePreviewItem>
          )
        })}
      </Group>
    )
  }, [files, handleRemoveFile, intl])

  const isAttachDisabled = disabled || isTranscribing

  const attachControl = useMemo(() => {
    if (!onFilesChange) return null

    return (
      <>
        {attachmentMode === 'menu' && (
          <ChatComposerAttachMenu
            onUploadClick={openFilePicker}
            onAddBookingClick={onAddBookingClick}
            showAddBookingAction={showAddBookingAction}
            disabled={isAttachDisabled}
          />
        )}
        {attachmentMode === 'picker' && (
          <IconButton
            kind={isRebrand ? 'primary' : 'tertiary'}
            variant="dark"
            shape="circle"
            size="medium"
            horizontalOutdent="start"
            onClick={openFilePicker}
            disabled={isAttachDisabled}
            aria-label={attachFilesLabel}
          >
            {PAPERCLIP_ICON}
          </IconButton>
        )}
      </>
    )
  }, [
    attachFilesLabel,
    attachmentMode,
    isAttachDisabled,
    isRebrand,
    onAddBookingClick,
    onFilesChange,
    openFilePicker,
    showAddBookingAction,
  ])

  const hiddenFileInput = onFilesChange ? (
    <HiddenFileInput
      type="file"
      ref={fileInputRef}
      onChange={handleFileSelect}
      multiple
      accept={ACCEPT_EXTENSIONS}
    />
  ) : null

  const messageTextArea = (
    <TextAreaWrapper className={clsx({
      stacked: isComposerStacked,
      'muted-placeholder': isStackedLayout,
      'hide-placeholder-on-focus': isMobile,
      'has-placeholder-overlay': !!placeholderOverlay,
    })}
    >
      <TextArea
        inputRef={textareaRef}
        placeholder={placeholderText}
        value={message}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        enterKeyHint={hasSoftKeyboard ? 'enter' : undefined}
        rows={ROWS}
        resize="none"
        noValidationSpacing
        autoFocus={autoFocus}
      />
      {!!placeholderOverlay && !isVoiceBusy && !message && <PlaceholderOverlay aria-hidden>{placeholderOverlay}</PlaceholderOverlay>}
    </TextAreaWrapper>
  )

  const stackableComposer = (
    <StackableComposer ref={stackableComposerRef} className={clsx({ stacked: isComposerStacked, 'wide-gap': isStackedLayout })}>
      <AttachSlot>{attachControl}</AttachSlot>
      <TextAreaSlot>{messageTextArea}</TextAreaSlot>
      <MicSlot>{!showStopButton && microphoneButton}</MicSlot>
      <SendSlot>{ctaButton}</SendSlot>
      {hiddenFileInput}
      {recordingOverlay}
    </StackableComposer>
  )

  const desktopComposer = (
    <Group direction="horizontal" gap={8} verticalAlign="center" fullWidth>
      {attachControl}
      {messageTextArea}
      {!showStopButton && microphoneButton}
      {ctaButton}
      {hiddenFileInput}
      {recordingOverlay}
    </Group>
  )

  const composerControls = usesStackableComposer ? stackableComposer : desktopComposer

  return (
    <Wrapper ref={wrapperRef}>
      {isVoicePromptOpen && voicePrompt === 'chatNudge' && (
        <VoiceChatNudge
          anchorElement={microphoneElement}
          textDirection={textDirection}
          overlay={overlayVoicePrompt}
          isClosing={isVoicePromptClosing}
          onAccept={handleMicrophoneClick}
          onDismiss={handleVoicePromptDismiss}
          onClosed={finishClosingVoicePrompt}
        />
      )}
      <ChatInputShell disabled={disabled} density={getShellDensity(isStackedLayout, isMobile)}>
        {chipsContent}
        {filesContent}
        {composerControls}
      </ChatInputShell>
      {isVoicePromptOpen && voicePrompt === 'landingTip' && (
        <VoiceLandingTip
          anchorElement={microphoneElement}
          textDirection={textDirection}
          isClosing={isVoicePromptClosing}
          onAccept={handleMicrophoneClick}
          onDismiss={handleVoicePromptDismiss}
          onClosed={finishClosingVoicePrompt}
        />
      )}
      {!hideDisclaimer && (
        <DisclaimerText
          variant="small"
          colour="light-foreground-alt"
          className={compactDisclaimer ? 'compact' : undefined}
        >
          {disclaimerText}
        </DisclaimerText>
      )}
    </Wrapper>
  )
}

export default ChatMessageInput
